import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Check, Crown, Sparkles, ArrowLeft, Clock, Infinity, Star, Zap, Tag } from "lucide-react";
import SEO from "../components/SEO";

const PricingPage = ({ auth }) => {
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      tier: "free",
      name: "Free",
      price: "CAD $0",
      originalPrice: null,
      period: "forever",
      description: "Try the full experience",
      duration: null,
      features: [
        "Access to all features (limited)",
        "1 mock test / simulation",
        "25 practice questions per day",
        "Study Hub & chapter summaries",
        "Flashcards preview",
        "Journey Tracker",
        "Study Plan Generator"
      ],
      notIncluded: [
        "Unlimited questions",
        "All 30 simulations",
        "All 3 mock tests",
        "Weak area analytics",
        "Full flashcard access"
      ],
      cta: auth.user ? "Current Plan" : "Start Free",
      disabled: auth.user?.subscription_tier === "free",
      highlight: false,
      tagline: "See everything CanadaTestPrep offers"
    },
    {
      tier: "pro",
      name: "Pro",
      price: "CAD $9.99",
      originalPrice: "CAD $14.99",
      period: "1 week",
      description: "Full access for 7 days",
      duration: "7 days",
      features: [
        "All 500+ practice questions",
        "All 30 simulation tests",
        "All 3 realistic mock exams",
        "200+ flashcards",
        "Weak area detection",
        "Full analytics & progress",
        "Study Plan Generator",
        "Notes & bookmarks",
        "Important Dates & Personalities",
        "Ad-free experience"
      ],
      notIncluded: [],
      cta: "Get 1 Week Access",
      disabled: false,
      highlight: true,
      tagline: "Perfect for last-minute prep",
      saleBadge: "SALE"
    },
    {
      tier: "premium",
      name: "Premium",
      price: "CAD $14.99",
      originalPrice: "CAD $29.99",
      period: "lifetime",
      description: "One payment, forever access",
      duration: "Lifetime",
      features: [
        "All 500+ practice questions",
        "All 30 simulation tests",
        "All 3 realistic mock exams",
        "200+ flashcards",
        "Weak area detection",
        "Full analytics & progress",
        "Study Plan Generator",
        "Notes & bookmarks",
        "Important Dates & Personalities",
        "Ad-free experience",
        "Future updates included",
        "Priority support"
      ],
      notIncluded: [],
      cta: "Get Lifetime Access",
      disabled: false,
      highlight: false,
      badge: "BEST VALUE",
      tagline: "Pay once, prepare forever",
      saleBadge: "50% OFF"
    }
  ];

  const handleUpgrade = async (tier) => {
    if (!auth.user) {
      window.location.href = "/register";
      return;
    }

    if (auth.user.subscription_tier === tier) {
      toast.info("You're already on this plan");
      return;
    }

    setLoading(tier);
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        tier,
        origin_url: window.location.origin
      }, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(null);
    }
  };

  const savings = ((9.99 * 4) - 14.99).toFixed(2); // Monthly equivalent savings

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <Link to={auth.user ? "/dashboard" : "/"} className="inline-flex items-center gap-2 text-slate-600 hover:text-maple mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          {auth.user ? "Back to Dashboard" : "Back to Home"}
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Pro and Premium unlock the exact same features. Choose based on how long you need to prepare.
          </p>
        </div>

        {/* Best Value Banner */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-6 mb-10 text-center shadow-lg">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Tag className="w-6 h-6 text-white" />
            <h2 className="font-heading text-xl font-bold text-white">
              Limited Time Sale: Lifetime Access at CAD $14.99
            </h2>
            <Tag className="w-6 h-6 text-white" />
          </div>
          <p className="text-white/90">
            <span className="line-through opacity-75">CAD $29.99</span> → <span className="font-bold">CAD $14.99</span> — Save 50%! Pay once, prepare forever.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan) => {
            const isCurrentPlan = auth.user?.subscription_tier === plan.tier;
            
            return (
              <Card 
                key={plan.tier}
                className={`relative border-2 transition-all ${
                  plan.badge 
                    ? 'border-yellow-400 shadow-xl ring-2 ring-yellow-200' 
                    : plan.highlight 
                      ? 'border-maple shadow-lg' 
                      : 'border-slate-200 hover:border-maple/50'
                }`}
                data-testid={`pricing-${plan.tier}`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-sm font-bold px-6 py-1.5 rounded-full shadow-md flex items-center gap-1">
                      <Crown className="w-4 h-4" />
                      {plan.badge}
                    </span>
                  </div>
                )}

                <CardHeader className="text-center pb-2 pt-6">
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                    plan.badge ? 'bg-yellow-100' : 'bg-maple-50'
                  }`}>
                    {plan.tier === "premium" ? (
                      <Infinity className={`w-7 h-7 ${plan.badge ? 'text-yellow-600' : 'text-maple'}`} />
                    ) : plan.tier === "pro" ? (
                      <Zap className="w-7 h-7 text-maple" />
                    ) : (
                      <Sparkles className="w-7 h-7 text-maple" />
                    )}
                  </div>
                  <CardTitle className="font-heading text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.tagline}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-5">
                  <div className="text-center">
                    {plan.originalPrice && (
                      <div className="mb-1">
                        <span className="text-lg text-slate-400 line-through">{plan.originalPrice}</span>
                        {plan.saleBadge && (
                          <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {plan.saleBadge}
                          </span>
                        )}
                      </div>
                    )}
                    <span className="font-heading text-4xl font-bold text-slate-900">{plan.price}</span>
                    <span className="text-slate-600 ml-1">/ {plan.period}</span>
                  </div>

                  {plan.duration && (
                    <div className="text-center">
                      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                        plan.duration === "Lifetime" 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {plan.duration === "Lifetime" ? (
                          <Infinity className="w-4 h-4" />
                        ) : (
                          <Clock className="w-4 h-4" />
                        )}
                        {plan.duration} access
                      </span>
                    </div>
                  )}

                  <ul className="space-y-2.5">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </li>
                    ))}
                    {plan.notIncluded?.map((feature, index) => (
                      <li key={`not-${index}`} className="flex items-start gap-2 opacity-50">
                        <span className="w-5 h-5 flex items-center justify-center text-slate-400 text-xs">✕</span>
                        <span className="text-sm text-slate-500 line-through">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full h-12 text-base font-semibold ${
                      plan.badge 
                        ? 'bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white shadow-md' 
                        : plan.highlight 
                          ? 'bg-maple hover:bg-maple-600 text-white' 
                          : plan.tier === "free" && auth.user
                            ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                            : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={(plan.tier === "free" && auth.user) || isCurrentPlan || loading === plan.tier}
                    data-testid={`upgrade-${plan.tier}-btn`}
                  >
                    {loading === plan.tier ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : (
                      plan.cta
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Comparison Note */}
        <div className="bg-slate-100 rounded-2xl p-6 mb-8">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 text-center">
            Pro vs Premium: Same Features, Different Duration
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-maple" />
                <span className="font-semibold">Pro ($9.99/week)</span>
              </div>
              <p className="text-slate-600">Best if your test is within the next week. Full access to prepare quickly.</p>
            </div>
            <div className="bg-white rounded-xl p-4 border-2 border-yellow-300">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-yellow-600" />
                <span className="font-semibold">Premium ($14.99/lifetime)</span>
              </div>
              <p className="text-slate-600">Best value! Prepare at your own pace, retake if needed, access future updates.</p>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-maple-50 border border-maple-100 rounded-2xl p-6 mb-8">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4 text-center">
            What's Included in Pro & Premium
          </h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="space-y-2">
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 500+ practice questions</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 200+ flashcards</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 30 simulation tests</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 3 realistic mock exams</p>
            </div>
            <div className="space-y-2">
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Weak area detection</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Study plan generator</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Progress analytics</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Notes & bookmarks</p>
            </div>
            <div className="space-y-2">
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Important Dates list</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Key Personalities list</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Chapter summaries</p>
              <p className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Journey Tracker</p>
            </div>
          </div>
        </div>

        {/* Money Back Guarantee */}
        <div className="text-center p-6 bg-green-50 border border-green-100 rounded-2xl">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
            98% Pass Rate Guarantee
          </h3>
          <p className="text-slate-600">
            Thousands of learners trust CanadaTestPrep for citizenship test preparation. 
            Our comprehensive question bank and study tools are designed to help you succeed.
          </p>
        </div>

        {/* FAQ */}
        <div className="mt-8 text-center">
          <p className="text-slate-600">
            Have questions? Check our{" "}
            <a href="/#faq" className="text-maple hover:underline">FAQ</a>
            {" "}or{" "}
            <Link to={auth.user ? "/contact" : "/register"} className="text-maple hover:underline">contact us</Link>
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default PricingPage;



